import axios from "axios";
import { router } from "../router";
import type {
	CourseKey,
	LoginCredentials,
	LoginResponse,
	SubscriptionCourseResponse,
} from "./api.types";
import type { UserProfileData } from "./userProfil.types";
import type { StudyWorkshopDetails } from "./studyWorkshopDetails.types";
import type { VimeoResponse } from "./vimeo.types";
import {
	getAccessToken,
	clearAuthData,
	getEmail,
} from "../utils/storage/authStorage";
import OneSignal from "onesignal-cordova-plugin";
import { Capacitor } from "@capacitor/core";

// Extend AxiosRequestConfig to include custom flag
declare module "axios" {
	export interface AxiosRequestConfig {
		skipAuthRedirect?: boolean;
	}
	export interface InternalAxiosRequestConfig {
		skipAuthRedirect?: boolean;
	}
}

// Create axios instance without default authorization header
const axiosInstanceV1 = axios.create({
	baseURL: import.meta.env.VITE_BASE_URL_V1,
});

const pushServiceInstance = axios.create({
	baseURL: import.meta.env.VITE_PUSH_SERVICE_URL,
});

// Add request interceptor to add authorization header dynamically
axiosInstanceV1.interceptors.request.use(
	async function (config) {
		const token = await getAccessToken();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	function (error) {
		return Promise.reject(error);
	}
);

axiosInstanceV1.interceptors.response.use(
	function (response) {
		return response;
	},
	async function (error: unknown) {
		const skipAuthRedirect =
			axios.isAxiosError(error) && error.config?.skipAuthRedirect === true;

		// Handle 401 unauthorized errors
		if (
			axios.isAxiosError(error) &&
			error.response?.status === 401 &&
			!skipAuthRedirect
		) {
			if (import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true" && Capacitor.isNativePlatform()) {
				OneSignal.logout();
			}
			await clearAuthData();
			const currentPath = router.latestLocation.pathname;
			if (currentPath.includes("auth") && currentPath.includes("workshops")) {
				router.history.back();
			} else if (currentPath.includes("login") === false) {
				await router.navigate({
					to: "/login",
				});
			}
		}
		// Offline API errors are expected - let components and OnlineStatusProvider handle them
		return Promise.reject(error);
	}
);

export interface ArticleData {
	ID: number;
	title: string;
	image: string;
	author: string;
	author_photo: string;
	date: string; // gotowe do wyswietlenia
	excerpt: string;
	kategorie: Array<{ term_id: number; name: string }>;
	tagi: Array<{ term_id: number; name: string }>;
	slug: string;
}

export interface ArticleListEndpoint {
	page: number;
	posts: Array<ArticleData>;
	total: number;
}

export interface ArticleParameters {
	search?: string;
	category?: string;
}

export interface ArticleInfiniteState {
	page: number;
	per_page: number;
}

export const getArticleList = async (
	parameters: ArticleParameters,
	pageParameter: number,
	perPage: number
): Promise<ArticleListEndpoint | undefined> => {
	const { search, category } = parameters;
	try {
		const { data } = await axiosInstanceV1.post<ArticleListEndpoint>(
			"/baza_wiedzy",
			{
				per_page: perPage,
				page: pageParameter,
				search,
				category,
			}
		);
		return data;
	} catch (er) {
		console.log("getArticleList", er);
		return undefined;
	}
};

export const getLatestArticle = async (): Promise<ArticleData | null> => {
	const data = await getArticleList({ search: "" }, 1, 1);
	return data?.posts?.length > 0 ? data.posts[0] : null;
};

export interface ArticleContentEndpoint {
	ID: number;
	title: string;
	image: string;
	date: number;
	content: string;
	kategorie: Array<{ term_id: number; name: string }>;
	tagi: Array<{ term_id: number; name: string }>;
	slug: string;
	author: {
		ID: string;
		first_name: string;
		last_name: string;
		profile_picture: string;
		position: string;
		email: string;
	};
}

export const getArticleContent = async (
	id: number
): Promise<ArticleContentEndpoint> => {
	try {
		const { data } = await axiosInstanceV1.post<ArticleContentEndpoint>(
			`/baza_wiedzy/${id}`
		);
		return data;
	} catch (er) {
		console.log("getArticleContent", er);
	}
};

export interface WorkshopParameters {
	search: string;
	category: string;
	viewStatus: "UPCOMING" | "RECORDED";
}

export interface EventParameters {
	search: string;
	category: string;
	sort_by?: string;
	sort?: string;
}

export interface Author {
	WP_user_ID: number;
	display_name: string;
	profile_picture: string;
	position: string;
	email: string;
	first_name: string;
	last_name: string;
	descritpion?: string;
	roles?: Array<string>;
}

export interface WorkshopData {
	nazwa: string; // V1
	date_of_publish: number; // V1
	finish_at: number | "NULL"; // V1
	starts_at: number | "NULL" | ""; // V1
	"legal-status": string;
	kategoria: Array<string>; // V1
	id: number; // V1
	ID: number; // V2
	title: string;
	content: string;
	excerpt: string;
	category: Array<{ id: number; name: string }>;
	date: number; // docelowo timestamp
	original_url: string;
	type: "product";
	authors_users_data: Array<Author>;
	slug: string;
	type_field: string;
}

export interface WorkshopListEndpoint {
	page: number;
	// posts: Array<WorkshopData>; V2
	items: Array<WorkshopData>; // V1
	total: number;
}

const workshopType = {
	trainingRecorded: 623,
	trainingUpcoming: 622,
	webinarRecorded: 642,
	webinarUpcoming: 624,
};

const getEventCode = (
	category: "training" | "webinar",
	switcher: "UPCOMING" | "RECORDED"
): number => {
	if (category === "training") {
		return switcher === "UPCOMING"
			? workshopType.trainingUpcoming
			: workshopType.trainingRecorded;
	}
	return switcher === "UPCOMING"
		? workshopType.webinarUpcoming
		: workshopType.webinarRecorded;
};

export const getEventList = async (
	parameters: EventParameters,
	eventCategory: "training" | "webinar",
	eventStatus: "UPCOMING" | "RECORDED",
	page: number,
	perPage: number
): Promise<WorkshopListEndpoint | undefined> => {
	const eventCode = getEventCode(eventCategory, eventStatus);
	const { search, category } = parameters;
	try {
		const { data } = await axiosInstanceV1.post<WorkshopListEndpoint>(
			"/events",
			{},
			{
				params: {
					per_page: perPage,
					page,
					search,
					type_ids: eventCode,
					category_ids: category,
					sort_by:
						eventCode === 623 || eventCode === 642
							? "legal-status"
							: "starts_at",
					sort: eventCode === 623 || eventCode === 642 ? "desc" : "asc",
					future: !(eventCode === 623 || eventCode === 642),
					// TODO: optional params futer - for workshopType === 623 || workshopType === 642 I don't need this params else futere: true
				},
			}
		);
		return data;
	} catch (er) {
		console.log("getEventList error", er);
		return undefined;
	}
};

export interface EventType {
	id: number;
	name: string;
	slug: string;
}

export interface WorkshopContentEndpoint {
	ID: number;
	title: string;
	content: string;
	date: number; // V2
	date_of_publish: number; // V1
	finish_at: number | "NULL"; // V1
	starts_at: number | "NULL" | ""; // V1
	"legal-status": number;
	tags: string;
	original_url: string;
	authors_users_data: Array<Author>;
	nazwa: string; // V1
	kategoria: Array<string>; // V1
	type_field: string;
	type: Array<EventType>;
	slug: string;
}

export const getEventContent = async (
	id: number
): Promise<WorkshopContentEndpoint | undefined> => {
	try {
		const { data } = await axiosInstanceV1.post<
			WorkshopContentEndpoint | undefined
		>(`/events/${id}`);
		return data;
	} catch (er) {
		console.log("getEventContent", er);
		return undefined;
	}
};

interface ArticleCategory {
	ID: number;
	name: string;
}

export interface ArticleCategoryV2 {
	ID: number;
	name: string;
}

export type ArticleCategoriesApiResponse = Record<string, ArticleCategoryV2>;

export const getArticleCategories = async (): Promise<
	Array<ArticleCategory> | undefined
> => {
	try {
		const { data } = await axiosInstanceV1.post<
			Array<ArticleCategory> | ArticleCategoriesApiResponse
		>("/baza_wiedzy/tematy");
		// Check if data is an array or an object with numeric string keys
		if (Array.isArray(data)) {
			// Already an array, return as is
			return data;
		} else {
			// It's an object with numeric string keys, convert to array
			return Object.values(data);
		}
	} catch (er) {
		console.log("getArticleCategories", er);
		return undefined;
	}
};

export const getWorkshopCategories = async (): Promise<
	Array<ArticleCategory> | undefined
> => {
	try {
		const { data } = await axiosInstanceV1.get<Array<ArticleCategory>>(
			"/workshop/categories"
		);
		return data;
	} catch (er) {
		console.log("getWorkshopCategories", er);
		return undefined;
	}
};

interface WorkshopCategory {
	id: number;
	nazwa: string;
}

export const getWorkshopCategoriesV1 = async (): Promise<
	Array<WorkshopCategory> | undefined
> => {
	try {
		const { data } =
			await axiosInstanceV1.post<Array<WorkshopCategory>>("/events/categories");
		return data;
	} catch (er) {
		console.log("getWorkshopCategoriesV1", er);
		return undefined;
	}
};

export interface NotificationInApp {
	title: string;
	body: string;
	route: string;
	createdAt: string;
}

export interface NotificationHistory {
	counterBadge: number;
	notifications: Array<NotificationInApp>;
}

export const getNotificationsHistory =
	async (): Promise<NotificationHistory> => {
		const email = await getEmail();
		const { data } = await axios.get<NotificationHistory>(
			`${import.meta.env.VITE_PUSH_SERVICE_URL}get-notifications-history`,
			{
				params: {
					email: email,
				},
			}
		);
		return data;
	};

export const clearNotificationsCounter =
	async (): Promise<NotificationHistory> => {
		const email = await getEmail();
		const { data } = await axios.get<NotificationHistory>(
			`${import.meta.env.VITE_PUSH_SERVICE_URL}clear-notifications-counter`,
			{
				params: {
					email: email,
				},
			}
		);
		return data;
	};

export const checkTokenFCMExistsInNotificationDB = async (
	token: string
): Promise<"NO_TOKEN_FOUND" | "TOKEN_FOUND"> => {
	const { data } = await axios.get<{
		result: "NO_TOKEN_FOUND" | "TOKEN_FOUND";
	}>(
		`${import.meta.env.VITE_PUSH_SERVICE_URL}check-token-fcm-exists-in-notification-db`,
		{
			params: {
				token: token,
			},
		}
	);
	return data.result;
};

export const saveUserForNotifications = async (
	email: string
): Promise<void> => {
	const { data } = await pushServiceInstance.get<void>(
		`/save-user-for-notifications`,
		{ params: { email } }
	);
	return data;
};

export const postLogin = async (
	credentials: LoginCredentials
): Promise<LoginResponse> => {
	const { data } = await axiosInstanceV1.post<LoginResponse>(
		`/auth/login`,
		credentials
	);
	return data;
};

export const getUserProfileData = async (): Promise<UserProfileData | null> => {
	try {
		const { data } =
			await axiosInstanceV1.get<UserProfileData>(`/auth/profile`);
		return data;
	} catch (er) {
		console.log("getUserProfileData error", er);
		return null;
	}
};

/**
 * Validate token without triggering authentication redirects
 * 
 * Used on app startup to check if stored token is still valid.
 * This function uses skipAuthRedirect flag to prevent automatic
 * redirect to login page on 401 error.
 * 
 * @see docs/TOKEN_VALIDATION.md for complete documentation
 * @returns 'valid' | 'invalid' | 'offline'
 */
export const validateToken = async (): Promise<
	"valid" | "invalid" | "offline"
> => {
	// Check if offline first
	if (!navigator.onLine) {
		console.log("[validateToken] Offline detected, trusting existing token");
		return "offline";
	}

	try {
		const token = await getAccessToken();
		if (!token) {
			return "invalid";
		}

		// Make a validation request with skipAuthRedirect flag
		const response = await axiosInstanceV1.head<UserProfileData>(
			`/auth/profile`,
			{
				skipAuthRedirect: true,
			}
		);

		if (response.status === 200) {
			// console.log("[validateToken] Token is valid");
			return "valid";
		}
		return "invalid";
	} catch (error) {
		console.log("[validateToken] Token validation failed:", error);
		// If we get 401, token is invalid
		if (axios.isAxiosError(error) && error.response?.status === 401) {
			return "invalid";
		}
		// For other errors (network issues, etc.), treat as offline
		return "offline";
	}
};

interface MyAcademyWorkshops {
	ownedCourses: Array<{
		id: number;
		name: string;
		slug: string;
	}>;
	futureduser: Array<{
		id: number;
		name: string;
		slug: string;
	}>;
}

export const myAcademyProfileData =
	async (): Promise<MyAcademyWorkshops | null> => {
		try {
			// let myAcademyWorkshops: MyAcademyWorkshops | null;
			const profilData =
				await axiosInstanceV1.get<UserProfileData>(`/auth/profile`);

			const workshopsFuturedUser = await Promise.all(
				Object.values(profilData.data.courses.futureduser).map(async (user) => {
					const data = await getEventContent(user.id);
					return data;
				})
			);

			// for every ownedCourses element I want make request to /v1/events to get workshop details
			const workshopsOwnedCourses = await Promise.all(
				Object.values(profilData.data.courses.ownedCourses).map(
					async (course) => {
						const data = await getEventContent(course.id);
						return data;
					}
				)
			);
			console.log("workshopsOwnedCourses", workshopsOwnedCourses);

			const myAcademyWorkshops: MyAcademyWorkshops = {
				ownedCourses: workshopsOwnedCourses,
				futureduser: workshopsFuturedUser,
			};

			return myAcademyWorkshops;
		} catch (er) {
			console.log("myAcademyProfileData error", er);
			return null;
		}
	};

export const getStudyCourseDetails = async (
	studyPath: string
): Promise<StudyWorkshopDetails | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<StudyWorkshopDetails>(
			`/auth/course-details/${studyPath}`
		);
		return data;
	} catch (er) {
		console.log("getStudyWorkshopDetails error", er);
		return undefined;
	}
};

export const getStudyWebinarDetails = async (
	studyPath: string
): Promise<StudyWorkshopDetails | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<StudyWorkshopDetails>(
			`/auth/webinar-details/${studyPath}`
		);
		return data;
	} catch (er) {
		console.log("getStudyWorkshopDetails error", er);
		return undefined;
	}
};

export const getStudyCourseUpcomingDetails = async (
	studyPath: string
): Promise<StudyWorkshopDetails | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<StudyWorkshopDetails>(
			`/auth/courseonline-details/${studyPath}`
		);
		return data;
	} catch (er) {
		console.log("getStudyWorkshopUpcomingDetails error", er);
		return undefined;
	}
};

export const getStudyWebinarUpcomingDetails = async (
	studyPath: string
): Promise<StudyWorkshopDetails | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<StudyWorkshopDetails>(
			`/auth/webinaronline-details/${studyPath}`
		);
		return data;
	} catch (er) {
		console.log("getStudyWorkshopUpcomingDetails error", er);
		return undefined;
	}
};

export const getStudyCourseUpcomingSubscription = async (
	slug: string
): Promise<StudyWorkshopDetails | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<StudyWorkshopDetails>(
			`/auth/courseonline-details/add-user/${slug}`
		);
		console.log("getStudyWorkshopUpcomingSubscription", data);
		return data;
	} catch (er) {
		console.log("getStudyWorkshopUpcomingDetails error", er);
		return undefined;
	}
};

export const getStudyWebinarUpcomingSubscription = async (
	slug: string
): Promise<StudyWorkshopDetails | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<StudyWorkshopDetails>(
			`/auth/webinaronline-details/add-user/${slug}`
		);
		console.log("getStudyWorkshopUpcomingSubscription", data);
		return data;
	} catch (er) {
		console.log("getStudyWorkshopUpcomingDetails error", er);
		return undefined;
	}
};

export const getVimeoVideoLink = async (
	vimeoId: string
): Promise<VimeoResponse | undefined> => {
	try {
		const { data } = await pushServiceInstance.get<VimeoResponse>(
			`/vimeo/video-links/${vimeoId}`
		);
		return data;
	} catch (er) {
		console.log("getVimeoVideoLink error", er);
		return undefined;
	}
};

export interface FavoriteArticles {
	favorites: Array<{
		wp_data: ArticleData & {
			wp_author: string;
			wp_date: string;
			wp_slug: string;
			wp_title: string;
			wp_excerpt: string;
		};
		slug: string;
	}>;
}

export const getFavoriteArticles =
	async (): Promise<FavoriteArticles | null> => {
		try {
			const { data } = await axiosInstanceV1.get<FavoriteArticles>(
				`/auth/favorites/posts`
			);
			data.favorites.forEach((favorite) => {
				favorite.wp_data.author = favorite.wp_data.wp_author;
				favorite.wp_data.date = favorite.wp_data.wp_date;
				favorite.wp_data.slug = favorite.wp_data.wp_slug;
				favorite.wp_data.title = favorite.wp_data.wp_title;
				favorite.wp_data.excerpt = favorite.wp_data.wp_excerpt;
			});
			return data;
		} catch (er) {
			console.log("getFavoriteArticles error", er);
			return null;
		}
	};

interface AddToFavoritesResponse {
	status: "added" | "removed";
}

export const getAddArticleToFavorites = async (
	articleSlug: string
): Promise<AddToFavoritesResponse | undefined> => {
	try {
		const { data } = await axiosInstanceV1.post<AddToFavoritesResponse>(
			`/auth/favorites/posts/${articleSlug}`
		);
		return data;
	} catch (er) {
		console.log("getFavoiriteArticles error", er);
		return undefined;
	}
};

export interface FavoriteWorkshops {
	favorites: Array<{
		wp_data: WorkshopData;
		type: number;
		slug: string;
	}>;
}

export const getFavoriteWorkshops =
	async (): Promise<FavoriteWorkshops | null> => {
		try {
			const { data } = await axiosInstanceV1.get<FavoriteWorkshops>(
				`/auth/favorites/products`
			);
			// Function to change property names
			// From: wp_author, wp_date, wp_slug, wp_title
			// To: author, date, slug, title

			return data;
		} catch (er) {
			console.log("getFavoiriteWorkshops error", er);
			return null;
		}
	};

export const getAddWorkshopToFavorites = async (
	workshopSlug: string
): Promise<AddToFavoritesResponse | null> => {
	try {
		const { data } = await axiosInstanceV1.post<AddToFavoritesResponse>(
			`/auth/favorites/products/${workshopSlug}`
		);
		return data;
	} catch (er) {
		console.log("getFavoiriteWorkshops error", er);
		return null;
	}
};

export const getCertificates = async (
	productId: number
): Promise<unknown | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<unknown>(
			`/auth/certificate/${productId}`
		);
		return data;
	} catch (er) {
		console.log("getCertificates error", er);
		return undefined;
	}
};

export const getAddOwnProduct = async (
	slug: string
): Promise<unknown | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<unknown>(
			`/auth/addownproduct/${slug}`
		);
		return data;
	} catch (er) {
		console.log("getAddOwnProduct error", er);
		return undefined;
	}
};

export const getSubscriptionCourse = async (
	courseKey: CourseKey
): Promise<SubscriptionCourseResponse | undefined> => {
	try {
		const { data } = await axiosInstanceV1.get<SubscriptionCourseResponse>(
			`/auth/subscription-course/${courseKey}`
		);
		return data;
	} catch (er) {
		console.log("getSubscriptionCourse error", er);
		return undefined;
	}
};

/**
 * Health check endpoint to verify internet connection
 * Returns a simple response to confirm connectivity
 */
export const checkConnection = async (): Promise<boolean> => {
	// console.log("[checkConnection] Starting connection check...");
	// console.log(
	// 	"[checkConnection] VITE_PUSH_SERVICE_URL:",
	// 	import.meta.env.VITE_PUSH_SERVICE_URL
	// );
	// console.log("[checkConnection] navigator.onLine:", navigator.onLine);

	try {
		// const startTime = Date.now();
		// // console.log("[checkConnection] Sending request to /hello...");

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const response = await pushServiceInstance.get("/hello", {
			timeout: 5000, // 5 sekund timeout
		});

		// const duration = Date.now() - startTime;
		// console.log(
		// 	"[checkConnection] Success:",
		// 	response.status,
		// 	"Duration:",
		// 	duration + "ms"
		// );
		return true;
	} catch (error) {
		const duration = Date.now() - (Date.now() - 5000);
		console.log("[checkConnection] Failed after", duration + "ms");
		console.log("[checkConnection] Error details:", error);
		if (error instanceof Error) {
			console.log("[checkConnection] Error message:", error.message);
			console.log("[checkConnection] Error name:", error.name);
		}
		return false;
	}
};
